import { ForceGraphConfig } from './src/charts/ForceGraph/config/forceGraphTypes';
import { MapChartConfig } from './src/charts/MapChart';

export interface Column {
    name: string;
    type: 'string' | 'number' | 'date' | 'boolean';
}

import type { DataPrepStepWithMeta } from './services/dataPrepTypes';

export interface Dataset {
    id: string;
    name: string;
    schema: Column[];
    sampleRows: Record<string, any>[];
    createdAt: string;
    ownerId?: string;
    updatedAt?: string;
    data?: any[];
    // Data Prep Pipeline
    prepSteps?: DataPrepStepWithMeta[];
}


export interface DataRelationship {
    id: string;
    datasetAId: string;
    datasetBId: string;
    fieldA: string;
    fieldB: string;
}

export type StandardChartType = 'bar' | 'line' | 'pie' | 'number' | 'area' | 'scatter' | 'radar' | 'kpi-card';
export type AdvancedChartType = 'globe' | 'force-directed' | '3d-bar' | 'map' | 'scientific' | 'heatmap' | 'vector' | '3d';

export type ChartType = StandardChartType | AdvancedChartType;

export type Aggregation = 'sum' | 'avg' | 'min' | 'max' | 'count';

export interface MeasureConfig {
    id: string;              // Stable UUID
    field: string;           // Column name
    aggregation: Aggregation;
    displayName: string;     // Legend/UI label
    isCustomLabel: boolean;  // True if user manually edited
    colorMode: 'auto' | 'custom';
    color?: string;          // Hex when colorMode = 'custom'
    visible: boolean;
}

export interface ScientificChartConfig {
    plotType: 'line' | 'scatter' | 'errorbar' | 'heatmap' | 'vector' | '3d-scatter' | '3d-surface' | '3d-wireframe';
    subplots: {
        rows: number;
        cols: number;
    };

    // Field Mappings
    fieldMappings: {
        xField?: string;
        yField?: string;
        zField?: string; // For 3D charts
        colorField?: string; // Categorical or numeric for color encoding
        sizeField?: string; // Numeric for marker size encoding
        seriesField?: string; // Categorical for splitting into multiple series
        // Error bar specific
        yErrorField?: string; // ± error
        xErrorField?: string; // For horizontal error bars
        yErrorUpperField?: string; // For asymmetric (exact upper bound)
        yErrorLowerField?: string; // For asymmetric (exact lower bound)
        // Confidence interval specific
        yUpperBoundField?: string;
        yLowerBoundField?: string;
        // Vector field specific
        uField?: string; // Horizontal vector component
        vField?: string; // Vertical vector component
    };

    axisOptions: {
        logScaleX: boolean;
        logScaleY: boolean;
        logScaleZ?: boolean; // For 3D
        grid: boolean;
        minorTicks: boolean;
        minorGrid: boolean;
        min?: number;
        max?: number;
        labelX?: string;
        labelY?: string;
        labelZ?: string; // For 3D
        axisFormat: 'plain' | 'scientific' | 'engineering';
        gridColor?: string;
        gridThickness?: number;
    };

    analysis: {
        trendline: 'none' | 'linear' | 'poly2' | 'poly3' | 'exp' | 'poly';
        trendlineDegree?: number; // For general poly
        showEquation: boolean;
        slopeMode: 'overall' | 'range';
        slopeRange: { min: number; max: number };
        confidenceInterval?: boolean;
        confidenceOpacity?: number;
    };

    errorBars: {
        show: boolean;
        mode: 'none' | 'constant' | 'field' | 'bounds'; // bounds = exact upper/lower fields
        constantValue: number;
        direction: 'symmetric' | 'positive' | 'negative' | 'asymmetric';
        capSize: number;
        lineThickness: number;
        color?: string;
        showConnectingLine?: boolean; // Connect points through error bar centers
    };

    transformations: {
        x: 'none' | 'log10' | 'ln';
        y: 'none' | 'log10' | 'ln' | 'sqrt';
        normalize: boolean;
        standardize: boolean;
    };

    style: {
        colormap: string; // viridis, plasma, turbo, etc.
        showColorbar: boolean;
        markerShape: 'circle' | 'square' | 'triangle' | 'cross' | 'star' | 'diamond';
        markerSize: number; // Base size
        markerSizeMin: number; // For size field scaling
        markerSizeMax: number; // For size field scaling
        markerOutline: boolean;
        markerFill?: string;
        lineStyle: 'solid' | 'dashed' | 'dotted';
        lineWidth: number; // 1-6
        curveSmoothing: boolean; // Interpolation spline
        alpha: number; // Opacity 0-1
        showPointMarkers: boolean; // For line plots
    };

    legend: {
        show: boolean;
        position: 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    };

    animation: {
        fadeIn: boolean;
        duration: number; // ms
    };

    // Heatmap/Contour specific
    heatmap: {
        colorScale: 'sequential' | 'diverging' | 'monochrome';
        resolution: number; // Grid size
        showContours: boolean;
        contourCount: number;
        interpolation: boolean; // Binning algorithm for uneven data
    };

    // Vector field specific
    vector: {
        scale: number;
        arrowSize: number;
        normalize: boolean;
        autoGrid: boolean; // Auto-grid for dense datasets
        magnitudeColoring: boolean;
    };

    // 3D specific
    threeD: {
        elevation: number;
        azimuth: number;
        projection: 'perspective' | 'orthographic';
        colorGradient: 'viridis' | 'plasma' | 'turbo' | 'inferno' | 'magma';
        pointSize: number;
        depthShading: boolean;
        showAxisBox: boolean;
        wireframeMode: boolean; // For surface plot
        meshDensity: number; // For surface/wireframe
    };

    export?: {
        resolution: 1 | 2 | 4;
    };
}


export interface ScientificOptions {
    mode?: 'line' | 'scatter';
    showTrendline: boolean;
    trendlineType: "linear" | "polynomial" | "exponential";
    showErrorBars: boolean;
    confidenceLevel?: number;
}

export interface VectorOptions {
    uField: string | null;
    vField: string | null;
    arrowScaling: number;
    normalize: boolean;
}

export interface ThreeDOptions {
    xField: string | null;
    yField: string | null;
    zField: string | null;
    pointSize?: number;
}

export interface ThreeDBarOptions {
    // Data Mapping
    zField?: string;

    // Layout
    mode?: 'grouped' | 'stacked' | 'single';
    groupGap?: number;
    barGap?: number;
    yScaleType?: 'linear' | 'log';

    // Visuals
    colorMode?: 'series' | 'value' | 'customField' | 'single';
    paletteId?: string;
    singleColor?: string;
    barOpacity?: number;
    barRoundness?: boolean;
    showGrid?: boolean;
    gridIntensity?: number;

    // Axes & Labels
    showXAxis?: boolean;
    showYAxis?: boolean;
    showZAxis?: boolean;
    showXLabels?: boolean;
    showZLabels?: boolean;
    xLabelFontSize?: number;
    zLabelFontSize?: number;
    xLabelAngle?: number;
    yTicks?: number;
    yTickFormat?: string;

    // Interaction
    enableHover?: boolean;
    enableSelection?: boolean;
    enableZoom?: boolean;
    enablePan?: boolean;
    constrainRotation?: boolean;
    cameraPreset?: 'isometric' | 'top' | 'side';

    // Legend
    legendType?: 'series' | 'value' | 'none';
    legendPosition?: 'tl' | 'tr' | 'bl' | 'br';
}

export interface GlobeOptions {
    // Data Mapping
    latField: string | null;
    lonField: string | null;
    valueField: string | null;
    categoryField: string | null;
    tooltipFields: string[];

    // Visual Settings
    pointSizeMin: number;
    pointSizeMax: number;
    colorMode: 'category' | 'gradient' | 'custom';
    customColor: string;
    globeTexture: 'day' | 'night' | 'topo';
    showAtmosphere: boolean;
    showLabels: boolean;

    // Interaction
    autoRotate: boolean;
    autoRotateSpeed: number;
    enableZoom: boolean;

    // Legend
    legendPosition: 'bottom-right' | 'bottom-left' | 'top-right';
    legendMode: 'category' | 'gradient' | 'minimal';
}

export interface MapOptions {
    latField?: string | null;
    lonField?: string | null;
    regionKeyField?: string | null;
    valueField?: string | null;
    geoMode?: "points" | "bubbles" | "choropleth";
}
// KPI Card Configuration
export interface KPICardConfig {
    // Field mappings handled in ChartConfig

    // Formatting
    valueFormat: 'plain' | 'currency' | 'percent' | 'compact';
    currencySymbol: string;
    decimalPlaces: number;

    // Display Options
    showDelta: boolean;
    showTrendline: boolean;
    showIcon: boolean;
    iconName: string;
    alignment: 'left' | 'center';
    cardSize: 'small' | 'medium' | 'large';

    // Delta Logic
    deltaMode: 'higher-is-better' | 'lower-is-better';

    // Sorting (for multiple cards with groupBy)
    sortBy: 'value' | 'label';
    sortOrder: 'asc' | 'desc';

    // Interaction
    enableClickToFilter: boolean;
}

export const DEFAULT_KPI_CONFIG: KPICardConfig = {
    valueFormat: 'plain',
    currencySymbol: '$',
    decimalPlaces: 0,
    showDelta: true,
    showTrendline: false,
    showIcon: false,
    iconName: 'target',
    alignment: 'center',
    cardSize: 'medium',
    deltaMode: 'higher-is-better',
    sortBy: 'value',
    sortOrder: 'desc',
    enableClickToFilter: true
};

export interface AdvancedConfig {
    scientific?: ScientificChartConfig;
    vector?: VectorOptions;
    threeD?: ThreeDOptions;
    threeDBar?: ThreeDBarOptions;
    map?: MapOptions;
    mapConfig?: MapChartConfig;
    globe?: GlobeOptions;
    forceGraph?: ForceGraphConfig;
    kpiCard?: KPICardConfig;
}

export interface ChartCustomizationConfig {
    xAxisLabel?: string;
    yAxisLabel?: string;
    color?: string;
    showLegend?: boolean;
    showDataLabels?: boolean;
    showGrid?: boolean;
    useMultipleColors?: boolean;
    paletteName?: string;
    customPalette?: string[];
    lineWidth?: number;
    areaOpacity?: number;
    roundedBars?: boolean;
    stacked?: boolean;
    // Scatter-specific
    pointSize?: number;
    pointOpacity?: number;
    labelContent?: 'auto' | 'name' | 'x' | 'y' | 'xy' | 'none';
    showQuadrants?: boolean;
    legendPosition?: 'right' | 'top' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

    // Annotations
    annotations?: {
        id: string;
        text: string;
        position: 'top' | 'bottom' | 'overlay';
        x?: number; // %
        y?: number; // %
        style: {
            fontSize: 'small' | 'medium' | 'large' | 'huge';
            color: string;
            isBold: boolean;
            isItalic: boolean;
            backgroundColor?: string;
        };
    }[];
}

export interface ChartConfig {
    id: string;
    datasetIds: string[];
    type: StandardChartType | AdvancedChartType;
    title: string;
    createdAt: string;
    isDraft?: boolean;

    // Standard chart fields
    xField?: string;
    yField?: string; // Deprecated
    sizeField?: string; // For scatter plots

    // Heatmap Specific
    xCategoryField?: string;
    yCategoryField?: string;
    imageField?: string;
    colorScale?: string;

    // KPI Card Specific
    kpiValueField?: string;      // Main value to display
    kpiLabelField?: string;      // Label for the KPI
    kpiComparisonField?: string; // Previous value for delta
    kpiGroupByField?: string;    // Split into multiple cards
    kpiTimeField?: string;       // For sparkline trend

    // New Measure System
    measures?: MeasureConfig[];

    // Deprecated - use measures instead
    ySeries?: Array<{
        field: string;
        label?: string;
        color?: string;
        aggregation: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'none';
        visible: boolean;
    }>;
    aggregation?: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'none'; // Deprecated, use ySeries aggregation

    // Facets
    facets?: {
        field: string;
        rows?: number;
        cols?: number;
    };

    // Series Encoding
    encoding?: {
        color?: { field: string };
        label?: { field: string };
        detail?: { field: string };
    };

    // Advanced chart fields
    latField?: string;
    lonField?: string;
    valueField?: string;
    sourceField?: string;
    targetField?: string;
    iso3Field?: string;
    countryField?: string;

    // Advanced chart configuration
    advanced?: AdvancedConfig;
    /** @deprecated Use advanced.scientific instead */
    scientificConfig?: ScientificChartConfig;

    // New customization object
    customization?: ChartCustomizationConfig;

    // Cross-chart interaction settings
    interactionSettings?: ChartInteractionSettings;
}

// Cross-chart interaction types
export interface ChartInteractionSettings {
    enableHighlightLinking: boolean;
    enableFilterLinking: boolean;
}

export interface LayoutItem {
    i: string; // instance id
    type: 'chart' | 'text' | 'line';
    chartId?: string;
    content?: string;
    x: number;
    y: number;
    w: number;
    h: number;
    description?: string;

    // Line widget properties
    lineOrientation?: 'horizontal' | 'vertical';
    lineStyle?: 'solid' | 'dashed' | 'dotted';
    lineColor?: string;
    lineThickness?: number;

    // Text widget properties
    fontSize?: 'small' | 'medium' | 'large' | 'huge';
    textAlign?: 'left' | 'center' | 'right';
    textColor?: string;
    isBold?: boolean;
    isItalic?: boolean;

    // Visual Customization
    backgroundColor?: string;
    backgroundGradient?: string; // e.g., "from-blue-500 to-purple-600"
    backgroundImage?: string; // URL or base64 data URL
    backgroundImageFit?: 'cover' | 'contain' | 'fill' | 'none';
    backgroundImageOpacity?: number; // 0-1
    borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
    borderWidth?: number; // 1-8px
    borderColor?: string; // hex color or Tailwind class
    borderStyle?: 'solid' | 'dashed' | 'dotted' | 'double';
    opacity?: number;

    // Chart Overlays (Annotations)
    annotations?: {
        id: string;
        type: 'text' | 'button';
        content: string;
        x: number; // Percentage 0-100
        y: number; // Percentage 0-100
        style?: {
            backgroundColor?: string;
            color?: string;
            fontSize?: string;
            padding?: string;
            borderRadius?: string;
        };
        onClick?: string; // For buttons
    }[];
}

export interface Dashboard {
    id: string;
    title: string;
    description?: string;
    layout: LayoutItem[];
    createdAt: string;
    updatedAt?: string;
}

export interface InsightData {
    // For correlations
    columnA?: string;
    columnB?: string;
    // For anomalies
    value?: any;
    context?: string; // The row where anomaly was found, as a JSON string
    // For distributions/top_values
    values?: { name: string; value: number }[];
}

export interface Insight {
    column: string;
    type: 'anomaly' | 'distribution' | 'correlation' | 'top_values' | 'general';
    title: string;
    summary: string;
    data?: InsightData;
}

export interface Explanation {
    title: string;
    meaning: string;
    importance: string;
    nextSteps: string;
}

export interface StorySlide {
    title: string;
    narrative: string;
    visualizationSuggestion: string;
    activeDataset: Dataset;
    activeDashboard: Dashboard | undefined;
    allCharts: ChartConfig[];
    insights: Insight[];
    insightsLoading: boolean;
    insightsError: string | null;
    activeTab: 'preview' | 'charts' | 'dashboard' | 'relationships';
    chartToEdit: ChartConfig | null;
    setActiveTab: (tab: 'preview' | 'charts' | 'dashboard' | 'relationships') => void;
    setActiveDatasetId: (id: string | null) => void;
    setActiveDashboardId: (id: string | null) => void;
    setEditingChartId: (id: string | null) => void;
    onGenerateInsights: () => void;
    onCreateChart: (chart: Omit<ChartConfig, 'id' | 'createdAt'>) => void;
    onUpdateChart: (chart: ChartConfig) => void;
    onDeleteChart: (chartId: string) => void;
    onCreateDashboard: () => void;
    onAddChartToDashboard: (chartId: string, dashboardId: string, position?: { x: number, y: number }) => void;
    onAddTextWidget?: (dashboardId: string, position?: { x: number, y: number, w: number, h: number }) => void;
    onAddLineWidget?: (dashboardId: string, orientation: 'horizontal' | 'vertical', position?: { x: number, y: number }) => void;
    onRemoveWidgetFromDashboard: (widgetInstanceId: string) => void;
    onUpdateDashboardLayout: (newLayout: LayoutItem[]) => void;
    onUpdateDashboardDetails: (details: Partial<Pick<Dashboard, 'title' | 'description'>>) => void;
    onDeleteDashboard: (dashboardId: string) => void;
    onGenerateChartFromInsight: (insight: Insight) => void;
    onAddChartToDashboardFromInsight: (insight: Insight) => void;
    onExplainInsight: (insight: Insight) => void;
    onCreateStoryFromInsight: (insight: Insight) => void;
    resetApp: () => void;
    isPro: boolean;
    openUpgrade: () => void;
    onContextMenu: (e: React.MouseEvent, type: 'chart' | 'dashboard', id: string) => void;
    onLogout: () => void;
    onNavigate: (path: string) => void;
};

export type View = 'upload' | 'workspace' | 'welcome';
export type WorkspaceTab = 'datasets' | 'relationships' | 'charts' | 'dashboard' | 'insights' | 'loader';

export interface WorkspaceProps {
    datasets: Dataset[];
    relationships: DataRelationship[];
    onCreateChart: (chart: Omit<ChartConfig, 'id' | 'createdAt'>) => void;
    onUpdateChart: (chart: ChartConfig) => void;
    onDeleteChart: (chartId: string) => void;
    chartToEdit: ChartConfig | null;
    [key: string]: any;
}
